import type { CredencialTablet, SesionEnLaTablet } from '../api/ops/tablet';

/**
 * Lo que la tablet guarda en el propio dispositivo (1.13): su credencial (`dev_….secreto`, la da `pairDevice` una sola vez)
 * en localStorage, para seguir emparejada tras reiniciar; y, aparte, la sesión corta del miembro que tecleó su PIN (la API
 * la cierra a los 10 minutos sin uso), en sessionStorage, que se pierde al cerrar la pestaña.
 */
/**
 * La credencial guardada. Las tablets emparejadas antes de G337 no guardaron `business_name` ni `device_name`: se leen
 * como opcionales y la tablet no dice dónde está emparejada hasta que se vuelva a emparejar.
 */
export type CredencialGuardada = Pick<CredencialTablet, 'device_id' | 'business_id' | 'credential'> & Partial<Pick<CredencialTablet, 'business_name' | 'device_name'>>;

export interface SesionMiembro {
    token: string;
    memberId: string;
    nombre: string;
    iniciales: string;
}

const CLAVE_CREDENCIAL = 'reservaria.tablet.credencial';
const CLAVE_SESION = 'reservaria.tablet.sesion';

type Almacen = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

function leer<T>(almacen: Almacen | null, clave: string, valido: (v: unknown) => v is T): T | null {
    try {
        const v: unknown = JSON.parse(almacen?.getItem(clave) ?? 'null');
        return valido(v) ? v : null;
    } catch {
        return null;
    }
}

function escribir(almacen: Almacen | null, clave: string, valor: unknown): void {
    try {
        if (valor === null) {
            almacen?.removeItem(clave);
        } else {
            almacen?.setItem(clave, JSON.stringify(valor));
        }
    } catch {
        // Sin almacenamiento: vive solo en memoria.
    }
}

const esObjeto = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;
const esCredencial = (v: unknown): v is CredencialGuardada => esObjeto(v) && typeof v.credential === 'string' && typeof v.device_id === 'string' && typeof v.business_id === 'string';
const esSesion = (v: unknown): v is SesionMiembro => esObjeto(v) && typeof v.token === 'string' && typeof v.memberId === 'string' && typeof v.nombre === 'string' && typeof v.iniciales === 'string';

function almacenSeguro(cual: 'localStorage' | 'sessionStorage'): Almacen | null {
    try {
        return typeof window === 'undefined' ? null : window[cual];
    } catch {
        return null;
    }
}

export interface AlmacenTablet {
    credencial: () => CredencialGuardada | null;
    guardarCredencial: (c: CredencialGuardada | null) => void;
    sesion: () => SesionMiembro | null;
    guardarSesion: (s: SesionMiembro | null) => void;
}

export function crearAlmacenTablet(local: Almacen | null = almacenSeguro('localStorage'), deSesion: Almacen | null = almacenSeguro('sessionStorage')): AlmacenTablet {
    let credencial = leer(local, CLAVE_CREDENCIAL, esCredencial);
    let sesion = leer(deSesion, CLAVE_SESION, esSesion);
    return {
        credencial: () => credencial,
        guardarCredencial: (c) => {
            credencial = c;
            escribir(local, CLAVE_CREDENCIAL, c);
            if (c === null) {
                sesion = null;
                escribir(deSesion, CLAVE_SESION, null);
            }
        },
        sesion: () => sesion,
        guardarSesion: (s) => {
            sesion = s;
            escribir(deSesion, CLAVE_SESION, s);
        },
    };
}

export type { SesionEnLaTablet };
